import { useCallback, useRef, useState } from 'react';
import { faBell } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import type { IFormDialogProps } from '~/components/molecules/FormDialog';
import type {
  IUserContentReviewNotifyFormProps,
  IUserContentReviewNotifyFormValues,
} from './UserContentReviewNotifyForm';
import { FormDialog } from '~/components/molecules/FormDialog';
import { UserContentReviewNotifyForm } from './UserContentReviewNotifyForm';

export type IUserContentReviewNotifyFormDialogProps = Pick<
  IFormDialogProps,
  'opened' | 'onClose'
> &
  IUserContentReviewNotifyFormProps;

export const UserContentReviewNotifyFormDialog: React.FC<
  IUserContentReviewNotifyFormDialogProps
> = (props) => {
  const { opened, onClose, onSubmit, ...other } = props;
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleClose = useCallback((): void => {
    setIsSubmitting(false);
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    onClose?.();
  }, [onClose]);

  const handleSubmit = useCallback(
    (values: IUserContentReviewNotifyFormValues): Promise<void> => {
      setIsSubmitting(true);

      return Promise.resolve(onSubmit?.(values))
        .then(handleClose)
        .finally(() => setIsSubmitting(false));
    },
    [onSubmit, handleClose],
  );

  return (
    <>
      <FormDialog
        opened={opened}
        onClose={handleClose}
        headline={t('UserContentReviewNotifyFormDialog.title')}
        submitButtonLabel={t('UserContentReviewNotifyFormDialog.notify')}
        size="md"
        submitting={isSubmitting}
        icon={<FontAwesomeIcon icon={faBell} />}
      >
        {({ formId }) => (
          <UserContentReviewNotifyForm
            id={formId}
            extended
            hideActionBar
            onSubmit={handleSubmit}
            {...other}
          />
        )}
      </FormDialog>
    </>
  );
};
